import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "fenceType", "openMapBtn", "areaStatus", "areaInput", "mapModal"]
  static values = { mode: String }

  connect() {
    this.areaSelected = false
    this.isEditMode = this.modeValue === "edit"
    
    this.initializeComponent()
    this.setupFormValidation()
  }

  t(key) {
    const locale = window.I18n?.locale || 'pt_BR'
    const translations = window.I18n?.translations?.[locale]?.javascript?.electronic_fences || {}
    return translations[key] || key
  }

  initializeComponent() {
    this.checkInitialAreaValue()
    this.setupInitialButtonState()
  }

  checkInitialAreaValue() {
    const areaValue = this.areaInputTarget?.value
    if (areaValue?.trim()) {
      try {
        const parsed = JSON.parse(areaValue)
        if (this.isValidAreaData(parsed)) {
          this.areaSelected = true
          this.showAreaStatus()
        }
      } catch (e) {
        this.areaSelected = false
      }
    }
  }

  setupInitialButtonState() {
    if (this.isEditMode) {
      const hasExistingArea = this.areaInputTarget?.value?.trim()
      const fenceType = this.fenceTypeTarget?.value

      if (hasExistingArea && fenceType && fenceType !== '' && fenceType !== 'prompt') {
        this.enableMapButton()
        this.areaSelected = true
        this.showAreaStatus()
      }
    }
  }

  fenceTypeChanged() {
    const fenceType = this.fenceTypeTarget?.value

    if (fenceType && fenceType !== '' && fenceType !== 'prompt') {
      this.enableMapButton()
    } else {
      this.disableMapButton()
    }
    
    this.clearError('fence-type-error')
  }

  openMap() {
    if (!this.validateFenceType()) return

    this.openMapModal()
  }

  openMapModal() {
    if (this.mapModalTarget) {
      this.mapModalTarget.classList.remove('hidden')

      setTimeout(() => {
        const mapController = window.mapController
        if (mapController?.fenceHandler) {
          mapController.loadExistingCoords()
        }
        mapController?.startDrawing()
      }, 500)
    }
  }

  closeModal() {
    window.mapController?.stopDrawing()
    this.mapModalTarget?.classList.add('hidden')
  }

  saveMap() {
    const areaValue = this.areaInputTarget?.value

    if (areaValue?.trim()) {
      try {
        const parsedData = JSON.parse(areaValue)

        if (this.isValidAreaData(parsedData)) {
          this.areaSelected = true
          this.showAreaStatus()
          this.clearError('area-error')
          this.closeModal()
          
          this.showToast(this.t('area_saved_success'))
          return
        }
      } catch (e) {
        console.error('Erro ao parsear dados da área:', e)
      }
    }
    
    this.showToast(this.t('area_draw_required'), 'error')
  }

  clearMap() {
    try {
      window.mapController?.fenceHandler?.clearAll()
      
      if (this.areaInputTarget) this.areaInputTarget.value = ''
      
      const fenceAreaInput = document.getElementById('fence_area')
      if (fenceAreaInput) fenceAreaInput.value = ''
      
      this.areaSelected = false
      this.hideAreaStatus()
      this.clearError('area-error')
      
      window.mapController?.reactivateDrawing?.()
      
      this.showToast(this.t('area_removed'), 'info')
    } catch (error) {
      console.error('Erro ao limpar mapa:', error)
      this.showToast(this.t('area_clear_error'), 'error')
    }
  }

  setupFormValidation() {
    this.formTarget?.addEventListener('submit', (e) => {
      if (!this.validateForm()) {
        e.preventDefault()
        e.stopPropagation()
        this.scrollToFirstError()
        return false
      }
      return true
    })
  }

  validateForm() {
    let hasErrors = false

    const validations = [
      { method: 'validateName', errorMethod: 'showNameError', errorId: 'name-error' },
      { method: 'validateFenceType', errorMethod: 'showFenceTypeError', errorId: 'fence-type-error' },
      { method: 'validateArea', errorMethod: 'showAreaError', errorId: 'area-error' }
    ]

    validations.forEach(({ method, errorMethod, errorId }) => {
      if (!this[method]()) {
        this[errorMethod]()
        hasErrors = true
      } else {
        this.clearError(errorId)
      }
    })

    return !hasErrors
  }

  validateName() {
    const nameInput = document.querySelector('input[name="electronic_fence[name]"]')
    return nameInput?.value.trim() !== ''
  }

  validateFenceType() {
    const fenceType = this.fenceTypeTarget?.value
    return fenceType && fenceType.trim() !== '' && fenceType !== 'prompt'
  }

  validateArea() {
    const areaValue = this.areaInputTarget?.value || document.getElementById('fence_area')?.value

    if (!areaValue?.trim()) return false

    try {
      const parsed = JSON.parse(areaValue)
      return this.isValidAreaData(parsed)
    } catch (e) {
      return false
    }
  }

  isValidAreaData(data) {
    if (!data) return false

    if (data.type === 'circle') {
      return data.center &&
             typeof data.center.lat === 'number' &&
             typeof data.center.lng === 'number' &&
             typeof data.radius === 'number' &&
             data.radius > 0
    }

    const coords = Array.isArray(data) ? data : data.coordinates
    if (!Array.isArray(coords) || coords.length < 3) return false

    return coords.every(coord =>
      coord && typeof coord.lat === 'number' && typeof coord.lng === 'number'
    )
  }

  enableMapButton() {
    if (this.openMapBtnTarget) {
      this.openMapBtnTarget.disabled = false
      this.openMapBtnTarget.classList.remove('opacity-50', 'cursor-not-allowed')
      this.openMapBtnTarget.classList.add('hover:bg-blue-700')
    }
  }

  disableMapButton() {
    if (this.openMapBtnTarget) {
      this.openMapBtnTarget.disabled = true
      this.openMapBtnTarget.classList.add('opacity-50', 'cursor-not-allowed')
      this.openMapBtnTarget.classList.remove('hover:bg-blue-700')
    }
  }

  showAreaStatus() {
    this.areaStatusTarget?.classList.remove('hidden')
  }

  hideAreaStatus() {
    this.areaStatusTarget?.classList.add('hidden')
  }

  showNameError() {
    this.showError('name-error', this.t('name_required'), 'input[name="electronic_fence[name]"]')
  }

  showFenceTypeError() {
    this.showError('fence-type-error', this.t('fence_type_required'), null, this.fenceTypeTarget?.parentElement)
  }

  showAreaError() {
    this.showError('area-error', this.t('area_required'), null, document.querySelector('.bg-blue-50.dark\\:bg-blue-900\\/20'))
  }

  showError(id, message, inputSelector = null, parentElement = null) {
    this.clearError(id)
    
    const parent = parentElement || document.querySelector(inputSelector)?.parentElement
    if (parent) {
      const errorDiv = document.createElement('div')
      errorDiv.id = id
      
      // Centralizar erro de área especificamente
      if (id === 'area-error') {
        errorDiv.className = 'mt-2 text-sm text-red-600 dark:text-red-400 font-medium text-center'
      } else {
        errorDiv.className = 'mt-2 text-sm text-red-600 dark:text-red-400 font-medium'
      }
      
      errorDiv.innerHTML = message
      parent.appendChild(errorDiv)
    }
  }

  clearError(id) {
    document.getElementById(id)?.remove()
  }

  scrollToFirstError() {
    const firstError = document.querySelector('[id$="-error"]')
    firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  showToast(message, type = 'success') {
    const existing = document.getElementById('fence-toast')
    if (existing) existing.remove()

    const colors = {
      success: 'bg-green-500',
      error: 'bg-red-500', 
      info: 'bg-blue-500'
    }
    
    const toast = document.createElement('div')
    toast.id = 'fence-toast'
    toast.className = `fixed top-4 right-4 z-[9999] ${colors[type]} text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-3 transition-all duration-300`
    toast.innerHTML = `<span class="font-medium">${message}</span>`

    document.body.appendChild(toast)
    setTimeout(() => {
      if (toast?.parentNode) {
        toast.style.transform = 'translateX(100%)'
        toast.style.opacity = '0'
        setTimeout(() => toast.parentNode?.removeChild(toast), 300)
      }
    }, 3000)
  }
}